import { theme } from '@resistapp/client/components/shared/theme';
import { PlotTooltip, usePlotTooltip } from '@resistapp/client/components/tooltips/plot-tooltip';
import { getKeyPressesFromMouseClick } from '@resistapp/client/utils/general';
import {
  chipViewHighlightSelectedValue,
  shouldDrawChipSelectionHighlight,
} from '@resistapp/client/utils/chip-view-highlight-mode';
import { useChipContext } from '@resistapp/client/views/chip-view/use-chip-context';
import { SampleSubType, SampleType } from '@resistapp/common/sampling-point-types';
import { QaLabelScope, QaLabelType, QaResolvedLabelSummary } from '@resistapp/common/types';
import { useTooltipInPortal } from '@visx/tooltip';
import { useMemo } from 'react';
import { ChipHeatmapTooltipContent, TooltipData } from './chip-heatmap-tooltip';
import {
  CT_QNT_COLORS,
  CT_THRESHOLDS,
  EFF_QNT_COLORS,
  EFF_THRESHOLDS,
  NO_AMPLIFICATION_COLOR,
} from './chip-view-constant';

export interface Cell {
  row: number;
  column: number;
  assay: string;
  sample: string;
  sampleName: string | null;
  sampleType: SampleType | null;
  sampleSubtype: SampleSubType | null;
  ct: number | null;
  tm: number | null;
  efficiency: number | null;
  flags: string | null;
  selectedAssays: string[] | null;
  rejectRule: number | 'M' | null;
  qaResultLabelType: QaLabelType | null;
  qaResultLabelScope: QaLabelScope | null;
  qaLabels: QaResolvedLabelSummary[];
}

interface Props {
  data: Cell[];
  width: number;
  height: number;
  selectedSamples: string[] | null;
  selectedAssays: string[] | null;
  showRejected?: boolean;
}

// HEATMAP_GRAY_FOR_GE_28 will use theme.colors.neutral300
const NULL_SELECTED_COLOR = '#ffffff';
const NULL_DESELECTED_COLOR = NULL_SELECTED_COLOR;

export function ChipHeatmap({
  data,
  width,
  height,
  selectedSamples,
  selectedAssays,

  showRejected = false,
}: Props) {
  const { handleCellClickStable, selectedMetric, highlightFlag, flaggedAssays, flaggedSamples } = useChipContext();
  // Rows/columns in the raw data may start from 0 or 1 depending on the export, so
  // we explicitly detect the minimum and maximum indices and derive counts from them.
  const minRow = useMemo(() => Math.min(...data.map(d => d.row)), [data]);
  const maxRow = useMemo(() => Math.max(...data.map(d => d.row)), [data]);
  const minCol = useMemo(() => Math.min(...data.map(d => d.column)), [data]);
  const maxCol = useMemo(() => Math.max(...data.map(d => d.column)), [data]);

  // Total number of distinct rows/cols present in the data
  const numRows = maxRow - minRow + 1;
  const numCols = maxCol - minCol + 1;

  // Cell dimensions - guard against division by zero if numRows / numCols somehow resolve to 0
  const cellWidth = numCols > 0 ? width / numCols : width;
  const cellHeight = numRows > 0 ? height / numRows : height;

  const tooltipStuff = useTooltipInPortal({ scroll: false, detectBounds: true });
  const { handleMouseMove, tooltipData, tooltipProps } = usePlotTooltip<TooltipData>(tooltipStuff);

  const containerRef = tooltipStuff.containerRef;

  const highlightAssaySet = useMemo(() => new Set(flaggedAssays), [flaggedAssays]);
  const highlightSampleSet = useMemo(() => new Set(flaggedSamples), [flaggedSamples]);
  const shouldDrawSelectedCellBorders = useMemo(
    () => shouldDrawChipSelectionHighlight(highlightFlag, selectedSamples, selectedAssays),
    [highlightFlag, selectedAssays, selectedSamples],
  );

  const cells = useMemo(
    () =>
      data.map(d => {
        const selected =
          (!selectedSamples || selectedSamples.includes(d.sample)) &&
          (!selectedAssays || selectedAssays.includes(d.assay));
        return { ...d, selected };
      }),
    [data, selectedSamples, selectedAssays],
  );

  const getFillColor = (cell: (typeof cells)[number]): string => {
    if (selectedMetric === 'ct' && (highlightAssaySet.has(cell.assay) || highlightSampleSet.has(cell.sample))) {
      return NO_AMPLIFICATION_COLOR;
    }
    switch (selectedMetric) {
      case 'efficiency': {
        const value = cell.efficiency;
        if (value === null || !Number.isFinite(value))
          return cell.selected ? NULL_SELECTED_COLOR : NULL_DESELECTED_COLOR;

        if (value < EFF_THRESHOLDS[0]) {
          return EFF_QNT_COLORS[0];
        } else if (value < EFF_THRESHOLDS[1]) {
          return EFF_QNT_COLORS[1];
        } else if (value < EFF_THRESHOLDS[2]) {
          return EFF_QNT_COLORS[2];
        } else if (value < EFF_THRESHOLDS[3]) {
          return EFF_QNT_COLORS[3];
        }
        return EFF_QNT_COLORS[4];
      }
      case 'ct':
      default: {
        const value = cell.ct;
        if (value === null || !Number.isFinite(value))
          return cell.selected ? NULL_SELECTED_COLOR : NULL_DESELECTED_COLOR;

        if (value >= CT_THRESHOLDS[5]) {
          // ct >= 28
          return theme.colors.neutral300 as string;
        } else if (value < CT_THRESHOLDS[0]) {
          // ct < 13
          return CT_QNT_COLORS[0];
        } else if (value < CT_THRESHOLDS[1]) {
          // 13 <= ct < 16
          return CT_QNT_COLORS[1];
        } else if (value < CT_THRESHOLDS[2]) {
          // 16 <= ct < 19
          return CT_QNT_COLORS[2];
        } else if (value < CT_THRESHOLDS[3]) {
          // 19 <= ct < 22
          return CT_QNT_COLORS[3];
        } else if (value < CT_THRESHOLDS[4]) {
          // 22 <= ct < 25
          return CT_QNT_COLORS[4];
        }
        // 25 <= ct < 28
        return CT_QNT_COLORS[5];
      }
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width, height }}>
      <svg width={width} height={height} style={{ display: 'block' }}>
        {cells.map(cell => {
          const fillColor = getFillColor(cell);
          const opacity = cell.selected ? 1 : 0.1;
          const x = (cell.column - minCol) * cellWidth;
          const y = (cell.row - minRow) * cellHeight;
          return (
            <g key={`${cell.row}-${cell.column}-${cell.sample}-${cell.assay}-${selectedMetric}`}>
              <rect
                x={x}
                y={y}
                width={cellWidth}
                height={cellHeight}
                fill={fillColor}
                opacity={opacity}
                onMouseEnter={e => {
                  handleMouseMove(e, cell, true);
                }}
                onMouseMove={e => {
                  handleMouseMove(e, cell);
                }}
                onMouseLeave={e => {
                  handleMouseMove(e, undefined);
                }}
                onClick={e => {
                  const keys = getKeyPressesFromMouseClick(e);
                  handleCellClickStable(cell.sample, cell.assay, keys, cell.selected);
                }}
              />
              {showRejected && cell.rejectRule != null && (
                <line
                  x1={x + 0.5}
                  y1={y + cellHeight - 0.5}
                  x2={x + cellWidth - 0.5}
                  y2={y + 0.5}
                  stroke="#000"
                  strokeWidth={1}
                  strokeOpacity={opacity}
                  pointerEvents="none"
                  shapeRendering="crispEdges"
                />
              )}
            </g>
          );
        })}

        {/* Overlay borders for either the explicit selection or a specific SmartChip flag */}
        {shouldDrawSelectedCellBorders &&
          cells
            .filter(cell => cell.selected)
            .map(cell => {
              const x = (cell.column - minCol) * cellWidth;
              const y = (cell.row - minRow) * cellHeight;
              return (
                <rect
                  key={`selected-border-${cell.row}-${cell.column}-${cell.sample}-${cell.assay}`}
                  x={x}
                  y={y}
                  width={cellWidth}
                  height={cellHeight}
                  fill="none"
                  stroke="#000000"
                  strokeWidth={1}
                  pointerEvents="none"
                  shapeRendering="crispEdges"
                />
              );
            })}
        {highlightFlag !== chipViewHighlightSelectedValue &&
          cells
            .filter(cell => cell.flags?.includes(highlightFlag))
            .map(cell => {
              const x = (cell.column - minCol) * cellWidth;
              const y = (cell.row - minRow) * cellHeight;
              const borderStrokeOpacity = cell.selected ? 1 : 0.1;
              return (
                <rect
                  key={`flag-border-${cell.row}-${cell.column}-${cell.sample}-${cell.assay}-${highlightFlag}`}
                  x={x}
                  y={y}
                  width={cellWidth}
                  height={cellHeight}
                  fill="none"
                  stroke="#000000"
                  strokeWidth={1}
                  strokeOpacity={borderStrokeOpacity}
                  pointerEvents="none"
                  shapeRendering="crispEdges"
                />
              );
            })}

        {/* Draw border last so it appears on top of the cells */}
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill="none"
          stroke={theme.colors.neutral700}
          strokeWidth={3}
          pointerEvents="none"
        />
      </svg>
      {tooltipData && (
        <PlotTooltip {...tooltipProps}>
          <ChipHeatmapTooltipContent d={tooltipData} />
        </PlotTooltip>
      )}
    </div>
  );
}
